<template>
    <modal-side-generic
    :headerColor="headerColor"
    :icon="headerBigIcon"
    :title="`${t('cards.cardlessWithdraw.myCodes.detailModal.modalTitle')} ${selectedCardlessWithdraw.statusDescription}`"
    :showFooter="selectedCardlessWithdraw.status === 'ACTIVE'"
    :buttonsFooterData="buttonsFooterData"
    @hideMe="emit('hideMe')">
        <!-- Loader Generic -->
        <loaders-generic v-if="showLoader" fullSize/>
        <!-- *********************************************
        *                                                *
        *                     Content                	 *
        *                                                *
        ********************************************** -->
        <div v-else class="group-wrapper">
            <divider-generic :applyBorder="false" icon="money" :title="t('cards.cardlessWithdraw.myCodes.detailModal.dividerLeftWithdraw')"/>
            <!-- Amount -->
            <text-block-generic
            :params="{
                label: t('cards.cardlessWithdraw.myCodes.detailModal.amount'),
                content: amountFormat(selectedCardlessWithdraw.amount, selectedCardlessWithdraw.currency),
                classes: selectedCardlessWithdraw.status === 'ACTIVE' ? ['extra-large', 'primary'] : null
            }"/>
            <!-- *********************************************
            *       WITHDRAW BLOCK INFO IF STATUS ACTIVE     *
            ********************************************** -->
            <div v-if="selectedCardlessWithdraw.status === 'ACTIVE'">
                <!-- Code -->
                <text-block-generic
                :rightButtonIcon="showInputValue ? 'eye-close' : 'eye-open'"
                :params="paramstextBlock"
                @rightButtonClicked="showContentButton()"/>
                <!-- Code expiryDate -->
                <text-block-generic
                :params="{
                    content: `${t('cards.cardlessWithdraw.myCodes.detailModal.expiryDate')}${getDateWithTime(getCardlessWidthdrawCodeDetail.expiryDate)}`,
                    classes: 'capitalize'
                }"/>
            </div>
            <!-- Status -->
            <text-block-generic
            :params="{ label: t('cards.cardlessWithdraw.myCodes.detailModal.status'), content: selectedCardlessWithdraw.statusDescription }"
            :icon="iconMapper(selectedCardlessWithdraw.status)"
            :colorIconPreset="iconColorMapper(selectedCardlessWithdraw.status)"/>
            <!-- Last Change Date -->
            <text-block-generic
            v-if="selectedCardlessWithdraw.status !== 'ACTIVE'"
            :params="{ label: lastChangeDateLabel, content: getDateWithTime(getCardlessWidthdrawCodeDetail.lastChangeDate), classes: 'capitalize' }"/>
            <!-- *********************************************
            *                    Card Info                   *
            ********************************************** -->
            <divider-generic
            applyBorder
            icon="credit-card" :title="t('cards.cardlessWithdraw.myCodes.detailModal.dividerLeftCard')"/>
            <!-- Card -->
            <text-block-generic
            :params="{ label: t('cards.cardlessWithdraw.myCodes.detailModal.card'), content: selectedCard.cardNumber }"/>
            <!-- *********************************************
            *                  Receiver Info                 *
            ********************************************** -->
            <divider-generic icon="person" :title="t('cards.cardlessWithdraw.myCodes.detailModal.dividerLeftProfile')"/>
            <text-block-generic
            :params="{ label: t('cards.cardlessWithdraw.myCodes.detailModal.msisdn'), content: getCardlessWidthdrawCodeDetail.destinationMsisdn }"/>
            <!-- *********************************************
            *                    Date Info                   *
            ********************************************** -->
            <divider-generic icon="calendar" :title="t('cards.cardlessWithdraw.myCodes.detailModal.dividerLeftDate')"/>
            <text-block-generic
            :params="{ label: t('cards.cardlessWithdraw.myCodes.detailModal.date'), content: getDateWithTime(getCardlessWidthdrawCodeDetail.creationDate), classes: 'capitalize'}"/>
        </div>
    </modal-side-generic>
    <!-- Feedback Toast -->
    <toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import ModalSideGeneric from '@/components/generic/modals/side/Generic'
import DividerGeneric from '@/components/generic/dividers/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import LoadersGeneric from '@/components/generic/loaders/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Vue & Helpers */
import { ref, computed } from 'vue'
import { amountFormat } from '@/assets/scripts/amounts'
import { getDateWithTime } from '@/assets/scripts/dateBuilders'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { iconMapper, iconColorMapper, iconColorMapperModal } from '@/assets/scripts/mappers/cardlessWithdraw'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['hideMe', 'cancelCardlessWithdraw'])

/* mapActions & mapGetters */
const { requestCardlessWidthdrawCodeDetail } = mapActions(moduleMapper.CARDS_CARDLESS_WITHDRAW_MY_CODES)
const { getCardsList, getCardlessWidthdrawCodeDetail } = mapGetters(moduleMapper.CARDS_CARDLESS_WITHDRAW_MY_CODES)

const props = defineProps({
    selectedCardlessWithdraw: Array,
    selectedCardId: String
})

/* Data */
const showLoader = ref(false)
const showInputValue = ref(false)

const selectedCard = computed(() => { return getCardsList.value.find(card => card.id === props.selectedCardId) })

const headerColor = computed(() => {
    return iconColorMapperModal(props.selectedCardlessWithdraw.status)
})

const headerBigIcon = computed(() => {
    return iconMapper(props.selectedCardlessWithdraw.status)
})

const paramstextBlock = ref({
    label: t('cards.cardlessWithdraw.myCodes.detailModal.withdrawCode'),
    content: t('cards.cardlessWithdraw.myCodes.detailModal.hideCode'),
    classes: ['primary', 'extra-large']
})

const secretCodeSplitted = () => {
    return getCardlessWidthdrawCodeDetail.value.otcode.slice(0, 3) + ' ' + getCardlessWidthdrawCodeDetail.value.otcode.slice(3, 6) + ' ' + getCardlessWidthdrawCodeDetail.value.otcode.slice(6, 9) + ' ' + getCardlessWidthdrawCodeDetail.value.otcode.slice(9, getCardlessWidthdrawCodeDetail.value.otcode.length)
}

const showContentButton = () => {
    showInputValue.value = !showInputValue.value
    paramstextBlock.value.content = showInputValue.value ? secretCodeSplitted() : t('cards.cardlessWithdraw.myCodes.detailModal.hideCode')
}

const buttonsFooterData = [{
    label: t('cards.cardlessWithdraw.myCodes.detailModal.cancelBtn'),
    buttonFullWidth: true,
	buttonType: 'negative',
	callbackFunction: () => { emit('cancelCardlessWithdraw') }
}]

const lastChangeDateLabel = computed(() => {
    const strings = {
        USED: t('cards.cardlessWithdraw.myCodes.detailModal.infoUsed'),
        FAILED: t('cards.cardlessWithdraw.myCodes.detailModal.infoError'),
        EXPIRED: t('cards.cardlessWithdraw.myCodes.detailModal.infoExpired'),
        CANCELLED: t('cards.cardlessWithdraw.myCodes.detailModal.infoCancelled')
    }

    return strings[props.selectedCardlessWithdraw.status]
})

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const requestDetails = async () => {
	showLoader.value = true

    const payload = {
        cardId: props.selectedCardId,
        codeId: props.selectedCardlessWithdraw.id
    }

	try {
        await requestCardlessWidthdrawCodeDetail(payload)
        showLoader.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
		showLoader.value = false
		applyErrorState.value = true
		errorHandler(error.response.data, requestDetails)
	}
}

/*************************************************
 *                                                *
 *             Success/Error Handlers             *
 *                                                *
 *************************************************/
const showFeedbackToast = ref(false)
const applyErrorState = ref(false)

const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
    feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

requestDetails()
</script>

<style lang="scss" scoped>
</style>
